import moment, { Moment } from "moment";
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import useClickOutside from "@/hooks/useClickOutSide";
import { ChevronLeft, ChevronRight, Calendar } from "lucide-react";
import { FORMATE } from "@/utils/constant/global.constant";

interface CustomDateRangePickerProps {
  value?: { startDate: string; endDate: string };
  onChange: (value: { startDate: string; endDate: string }) => void;
  placeholder?: string;
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  maxToday?: boolean;
  className?: string;
}

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({
  value,
  onChange,
  placeholder = "Select date range",
  disabled = false,
  minDate,
  maxDate,
  maxToday = false,
  className = "",
}) => {
  const [currentDate, setCurrentDate] = useState<Moment>(moment());
  const [showDropdown, setDropdown] = useState<"startDate" | "endDate" | "">("");
  const [errors, setErrors] = useState<{ startDate?: string; endDate?: string }>({});
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const dropdownRef = useClickOutside(() => {
    setDropdown("");
  });
  const startDateInputRef = useRef<HTMLInputElement>(null);
  const endDateInputRef = useRef<HTMLInputElement>(null);

  const handleNavigation = (direction: "prev" | "next") => {
    setCurrentDate(direction === "next" ? moment(currentDate).add(1, "month") : moment(currentDate).subtract(1, "month"));
  };

  const isValidDate = (date: Moment, fieldType: "startDate" | "endDate") => {
    const minDateMoment = minDate ? moment(minDate) : null;
    const maxDateMoment = maxDate ? moment(maxDate) : null;
    const today = moment();

    let isValid = true;

    // Check global date constraints
    if (minDateMoment && !date.isSameOrAfter(minDateMoment, "day")) {
      isValid = false;
    }

    if (maxDateMoment && !date.isSameOrBefore(maxDateMoment, "day")) {
      isValid = false;
    }

    if (maxToday && !date.isSameOrBefore(today, "day")) {
      isValid = false;
    }

    // For end date, it must be after start date
    if (fieldType === "endDate" && value?.startDate) {
      const startDate = moment(value.startDate);
      if (!date.isSameOrAfter(startDate, "day")) {
        isValid = false;
      }
    }

    return isValid;
  };

  const generateCalendarDays = (fieldType: "startDate" | "endDate") => {
    const firstDayOfMonth = moment(currentDate).startOf("month");
    const lastDayOfPrevMonth = moment(currentDate).subtract(1, "month").endOf("month");
    const startDay = firstDayOfMonth.day();

    const days = [];

    // Previous month days
    for (let i = startDay - 1; i >= 0; i--) {
      const prevDate = moment(lastDayOfPrevMonth).subtract(i, "days");
      days.push({
        date: lastDayOfPrevMonth.date() - i,
        isCurrentMonth: false,
        month: "prev",
        isValid: isValidDate(prevDate, fieldType),
      });
    }

    // Current month days
    const daysInMonth = currentDate.daysInMonth();
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDay = moment(currentDate).date(i);
      days.push({
        date: i,
        isCurrentMonth: true,
        month: "current",
        isValid: isValidDate(currentDay, fieldType),
      });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = moment(currentDate).add(1, "month").date(i);
      days.push({
        date: i,
        isCurrentMonth: false,
        month: "next",
        isValid: isValidDate(nextDate, fieldType),
      });
    }

    return days;
  };

  const validateDates = (startDate: string, endDate: string) => {
    const newErrors: { startDate?: string; endDate?: string } = {};

    if (startDate && endDate) {
      const start = moment(startDate);
      const end = moment(endDate);

      if (!end.isSameOrAfter(start, "day")) {
        newErrors.endDate = "End date must be after start date";
      }
    }

    if (startDate && minDate) {
      const start = moment(startDate);
      const min = moment(minDate);
      if (!start.isSameOrAfter(min, "day")) {
        newErrors.startDate = `Start date must be after ${min.format(FORMATE.DATE)}`;
      }
    }

    if (endDate && maxDate) {
      const end = moment(endDate);
      const max = moment(maxDate);
      if (!end.isSameOrBefore(max, "day")) {
        newErrors.endDate = `End date must be before ${max.format(FORMATE.DATE)}`;
      }
    }

    if (maxToday) {
      const today = moment();
      if (startDate && !moment(startDate).isSameOrBefore(today, "day")) {
        newErrors.startDate = "Start date cannot be in the future";
      }
      if (endDate && !moment(endDate).isSameOrBefore(today, "day")) {
        newErrors.endDate = "End date cannot be in the future";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDateSelect = (date: Moment, fieldType: "startDate" | "endDate") => {
    const dateString = date.format("YYYY-MM-DD");
    const newValue = {
      startDate: fieldType === "startDate" ? dateString : value?.startDate || "",
      endDate: fieldType === "endDate" ? dateString : value?.endDate || "",
    };

    // Validate the new date range
    if (validateDates(newValue.startDate, newValue.endDate)) {
      onChange(newValue);
    } else {
      // Still update the value but with errors
      onChange(newValue);
    }

    setDropdown("");
  };

  const getYearRange = (fieldType: "startDate" | "endDate") => {
    const currentYear = moment().year();
    let minYear = minDate ? moment(minDate).year() : currentYear - 100;
    const maxYear = maxDate ? moment(maxDate).year() : maxToday ? currentYear : currentYear + 10;

    // For end date, minimum year should be based on start date if available
    if (fieldType === "endDate" && value?.startDate) {
      const startYear = moment(value.startDate).year();
      minYear = Math.max(minYear, startYear);
    }

    return Array.from({ length: maxYear - minYear + 1 }, (_, i) => minYear + i);
  };

  const formatDuration = () => {
    if (!value?.startDate || !value?.endDate) return null;
    const duration = moment.duration(moment(value.endDate).diff(moment(value.startDate)));
    const years = duration.years();
    const months = duration.months();
    const days = duration.days();

    let result = "";
    if (years > 0) result += `${years} Year${years > 1 ? "s" : ""} `;
    if (months > 0) result += `${months} Month${months > 1 ? "s" : ""} `;
    if (days > 0 && !years && !months) result += `${days} Day${days > 1 ? "s" : ""} `;

    return result.trim();
  };

  // Function to check available space and determine if calendar should open upward
  const checkSpaceAndPosition = () => {
    const activeInputRef = showDropdown === "startDate" ? startDateInputRef : endDateInputRef;
    if (!activeInputRef.current) return;
    
    const inputRect = activeInputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const calendarHeight = 350; // Approximate height of the calendar
    const calendarWidth = 256; // Minimum width of the calendar
    const buffer = 20; // Small buffer to ensure calendar doesn't touch viewport edges
    const spaceBelow = viewportHeight - inputRect.bottom - buffer;
    const spaceAbove = inputRect.top - buffer;
    
    // Determine if calendar should open upward
    const shouldOpenUpward = spaceBelow < calendarHeight && spaceAbove > calendarHeight;
    
    // Calculate vertical position
    let top = shouldOpenUpward ? inputRect.top - calendarHeight - 8 : inputRect.bottom + 8;
    
    // Ensure top position doesn't go above viewport
    if (top < buffer) {
      top = buffer;
    }
    
    // Ensure bottom doesn't go below viewport
    if (top + calendarHeight > viewportHeight - buffer) {
      top = viewportHeight - calendarHeight - buffer;
    }
    
    // Calculate horizontal position
    let left = inputRect.left;
    let width = Math.max(inputRect.width, calendarWidth);
    
    // Ensure left position doesn't go outside viewport
    if (left < buffer) {
      left = buffer;
    }
    
    // Ensure right doesn't go outside viewport
    if (left + width > viewportWidth - buffer) {
      left = viewportWidth - width - buffer;
    }
    
    setDropdownPosition({ top, left, width });
  };

  // Check space when calendar opens
  useEffect(() => {
    if (showDropdown) {
      checkSpaceAndPosition();
    }
  }, [showDropdown]);

  // Recalculate position on window resize
  useEffect(() => {
    const handleResize = () => {
      if (showDropdown) {
        checkSpaceAndPosition();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [showDropdown]);

  return (
    <div className={`relative w-full ${className}`}>
      <div className="flex flex-col text-13 gap-2">
        {/* Start Date and End Date Inputs */}
        <div className="flex gap-2 items-start w-full">
          {/* Start Date */}
          <div className="flex flex-col gap-1 w-full">
            <label className="text-xs text-gray-700">Start date</label>
            <div className="relative">
              <input
                ref={startDateInputRef}
                type="text"
                value={value?.startDate ? moment(value.startDate).format(FORMATE.DATE) : ""}
                onClick={() => {
                  if (value?.startDate) {
                    setCurrentDate(moment(value.startDate));
                  }
                  setDropdown("startDate");
                }}
                placeholder={placeholder}
                readOnly
                disabled={disabled}
                className={`
                  w-full px-3 py-2 text-text-default rounded-lg outline-none border
                  ${errors.startDate ? "border-red-500" : "border-fieldBorder"}
                  ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
                  hover:border-gray-400 focus:border-text-primary-500
                `}
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <Calendar className="text-gray-400 size-4" />
              </span>
            </div>
            {errors.startDate && <span className="text-xs text-red-500">{errors.startDate}</span>}
          </div>

          {/* End Date */}
          <div className="flex flex-col gap-1 w-full">
            <label className="text-xs text-gray-700">End date</label>
            <div className="relative">
              <input
                ref={endDateInputRef}
                type="text"
                value={value?.endDate ? moment(value.endDate).format(FORMATE.DATE) : ""}
                onClick={() => {
                  if (value?.endDate) {
                    setCurrentDate(moment(value.endDate));
                  }
                  setDropdown("endDate");
                }}
                placeholder={placeholder}
                readOnly
                disabled={disabled}
                className={`
                  w-full px-3 py-2 text-text-default rounded-lg outline-none border
                  ${errors.endDate ? "border-red-500" : "border-fieldBorder"}
                  ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"}
                  hover:border-gray-400 focus:border-text-primary-500
                `}
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <Calendar className="text-gray-400 size-4" />
              </span>
            </div>
            {errors.endDate && <span className="text-xs text-red-500">{errors.endDate}</span>}
          </div>
        </div>

        {/* Duration Display */}
        {formatDuration() && <div className="text-xs text-gray-600 mt-1">Duration: {formatDuration()}</div>}

        {/* Calendar Dropdown */}
        {showDropdown && createPortal(
          <div
            ref={dropdownRef}
            className="fixed border rounded-lg shadow-lg z-[9999] bg-white"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              minWidth: '256px'
            }}
          >
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4 border-b p-2 py-4">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigation("prev");
                }}
                className="hover:bg-gray-100 p-1 rounded"
              >
                <ChevronLeft className="text-gray-600 size-4" />
              </button>
              <div className="flex justify-center flex-1 gap-2">
                <select
                  value={currentDate.year()}
                  onChange={(e) => setCurrentDate(moment(currentDate).year(parseInt(e.target.value)))}
                  className="px-2 py-1 text-13 rounded border border-gray-300 focus:border-blue-500 outline-none"
                >
                  {getYearRange(showDropdown).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
                <select
                  value={currentDate.month()}
                  onChange={(e) => setCurrentDate(moment(currentDate).month(parseInt(e.target.value)))}
                  className="px-2 py-1 text-13 rounded border border-gray-300 focus:border-blue-500 outline-none"
                >
                  {moment.months().map((month, index) => (
                    <option key={month} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleNavigation("next");
                }}
                className="hover:bg-gray-100 p-1 rounded"
              >
                <ChevronRight className="text-gray-600 size-4" />
              </button>
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1 p-2">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <div key={day} className="text-center font-medium py-1 text-gray-600 text-xs">
                  {day}
                </div>
              ))}
              {generateCalendarDays(showDropdown).map((day, index) => {
                const dayMoment = moment(currentDate).clone();
                if (day.month === "prev") {
                  dayMoment.subtract(1, "month");
                } else if (day.month === "next") {
                  dayMoment.add(1, "month");
                }
                dayMoment.date(day.date);

                const isSelected =
                  showDropdown === "startDate"
                    ? value?.startDate && moment(value.startDate).isSame(dayMoment, "day")
                    : value?.endDate && moment(value.endDate).isSame(dayMoment, "day");

                return (
                  <button
                    key={index}
                    disabled={!day.isValid}
                    onClick={() => handleDateSelect(dayMoment, showDropdown)}
                    className={`
                      p-2 text-center text-xs
                      ${!day.isCurrentMonth ? "text-gray-400" : "text-gray-700"}
                      ${!day.isValid ? "opacity-50 cursor-not-allowed bg-gray-100" : ""}
                      ${isSelected ? "bg-blue-500 text-white" : "hover:bg-gray-100"}
                    `}
                  >
                    {day.date}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
      </div>
    </div>
  );
};

export default CustomDateRangePicker;
